// THE ATTACHMENT READS AND WRITES (spec 09 §2.29, §5's E2-2 rows 10 to 13, §7
// narrowing 19; D-323 rulings 8 to 12, D-335). Module 09's half is four
// routes; the file itself goes through the platform registry's three steps
// first (`POST /v1/files`, the presigned PUT, `POST /v1/files/{id}/store`).
//
// ONE LIST PER SUBJECT. `GET /v1/sales-attachments` names exactly one of
// `customer_id`, `quote_id` and `sales_order_id`, newest first, twenty a page
// with Show more (`useInfiniteQuery` on `next_cursor`). `withheld_count`
// counts the subject's `commercial` files the reader is not shown, over the
// whole subject and never one page of it, so the panel reads it from the
// first page. The key carries `include_detached`, because the two lists are
// different reads and a toggle must never paint one from the other's cache.
//
// AFTER A WRITE, READ THE LIST AGAIN. The attach and the detach store their
// answers without `title` (`@IdempotentRedact(["title"])`), so a replay
// returns the row with no title. A screen that painted the write's answer
// would show an untitled file after a lost response; every write here
// invalidates the subject's lists instead and paints nothing from its answer.
//
// THE UPLOAD WIRE IS INJECTABLE (`AttachmentUploadWire`), so the panel's
// unit tests drive its state machine without a network, and the defaults are
// the real calls. The PUT is an `XMLHttpRequest`, because `fetch` cannot
// report upload progress and §4.2 asks the upload to show its progress (the
// import panel's reason, areas/onboarding/screens/imports/upload-panel.tsx).

import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { components } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { RequestError, newIdempotencyKey, unwrap } from "../../../../lib/request";
import { startDownload } from "../components/attachments/attachment-model";
import type { AttachmentSubjectKind } from "./permissions";

type S = components["schemas"];

export type SalesAttachment = S["SalesAttachment"];
export type SalesAttachmentListResponse = S["SalesAttachmentListResponse"];

/** The subject an attachment panel lists and writes. */
export interface AttachmentSubject {
  readonly kind: AttachmentSubjectKind;
  readonly id: string;
}

/** The query and body key each subject is named by on the wire. */
export const SUBJECT_KEY = {
  customer: "customer_id",
  quote: "quote_id",
  sales_order: "sales_order_id",
} as const satisfies Record<AttachmentSubjectKind, string>;

/** Twenty a page with Show more (§4.2's E2-2a2 annotation). */
export const ATTACHMENTS_PAGE = 20;

export const attachmentKeys = {
  /** Every list of one subject, whichever statuses it asked for. */
  subject: (subject: AttachmentSubject) =>
    ["sales", "attachments", subject.kind, subject.id] as const,
  list: (subject: AttachmentSubject, includeDetached: boolean) =>
    [...attachmentKeys.subject(subject), includeDetached ? "all" : "active"] as const,
};

export function useAttachments(
  subject: AttachmentSubject,
  includeDetached: boolean,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: attachmentKeys.list(subject, includeDetached),
    enabled: enabled && subject.id.length > 0,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) => {
      const res = await client.GET("/v1/sales-attachments", {
        signal,
        params: {
          query: {
            [SUBJECT_KEY[subject.kind]]: subject.id,
            limit: ATTACHMENTS_PAGE,
            ...(includeDetached ? { include_detached: "true" as const } : {}),
            ...(pageParam !== undefined ? { cursor: pageParam } : {}),
          },
        },
      });
      return unwrap(res, "Could not load the attached files");
    },
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

/** Detach keeps the file and the link (§2.29), and a detached file answers
 * 200 again, so a fresh key per press is safe: a second press after a lost
 * response writes nothing new. The list is read again either way. */
export function useDetachAttachment(subject: AttachmentSubject) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (attachmentId: string) => {
      const res = await client.POST("/v1/sales-attachments/{id}/detach", {
        params: {
          path: { id: attachmentId },
          header: { "Idempotency-Key": newIdempotencyKey() },
        },
      });
      return unwrap(res, "Could not detach the file");
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: attachmentKeys.subject(subject) });
    },
  });
}

/** Mint the five-minute URL (§5's E2-2 row 12) and hand it to the browser.
 * Every refusal here is 404 `ATTACHMENT_NOT_FOUND`, a withheld file
 * included, so the panel reads the list again after one: the file it showed
 * may have been detached or moved by someone else. `open` is injectable for
 * tests. */
export function useAttachmentDownload(
  subject: AttachmentSubject,
  open: (url: string) => void = startDownload,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (attachmentId: string) => {
      const res = await client.GET("/v1/sales-attachments/{id}/download", {
        params: { path: { id: attachmentId } },
      });
      return unwrap(res, "Could not open the file");
    },
    onSuccess: (answer) => {
      open(answer.url);
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: attachmentKeys.subject(subject) });
    },
  });
}

// ---------------------------------------------------------------------------
// The upload: the registry's three steps, then the attach
// ---------------------------------------------------------------------------

export interface CreatedUpload {
  readonly fileId: string;
  readonly uploadUrl: string;
  /** The type the registry wrote on the row, which the PUT must carry: an
   * alias (`image/vnd.dwg`) arrives back as `application/acad` (§2.29). */
  readonly contentType: string;
}

export interface AttachBody {
  readonly fileId: string;
  readonly subject: AttachmentSubject;
  readonly category: "technical" | "commercial";
  readonly title: string | undefined;
}

/** The four calls, each throwing `RequestError` on a refusal and a plain
 * `Error` on a transport failure. */
export interface AttachmentUploadWire {
  createFile(
    input: {
      readonly contentType: string;
      readonly byteSize: number;
      readonly sha256: string;
      readonly fileName: string;
    },
    idempotencyKey: string,
  ): Promise<CreatedUpload>;
  putBytes(
    upload: CreatedUpload,
    bytes: Blob,
    onProgress: (sent: number) => void,
    signal: AbortSignal,
  ): Promise<void>;
  storeFile(fileId: string, idempotencyKey: string): Promise<void>;
  /** Resolves with nothing: a replay of the attach carries no `title`
   * (spec 09 §2.29, D-339), so the panel reads the list again. */
  attach(body: AttachBody, idempotencyKey: string): Promise<void>;
}

/** The PUT's own failure: a transport error or a non-2xx from the object
 * store. No registry row changed, so a second press resends the bytes. */
export class UploadTransferError extends Error {
  constructor(readonly status: number | undefined) {
    super(status === undefined ? "upload failed" : `upload answered ${String(status)}`);
    this.name = "UploadTransferError";
  }
}

/** The PUT as an XHR, reporting progress. Exported for its unit test. */
export function putWithProgress(
  upload: CreatedUpload,
  bytes: Blob,
  onProgress: (sent: number) => void,
  signal: AbortSignal,
  makeXhr: () => XMLHttpRequest = () => new XMLHttpRequest(),
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = makeXhr();
    xhr.open("PUT", upload.uploadUrl);
    xhr.setRequestHeader("Content-Type", upload.contentType);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(bytes.size);
        resolve();
      } else {
        reject(new UploadTransferError(xhr.status));
      }
    };
    xhr.onerror = () => {
      reject(new UploadTransferError(undefined));
    };
    xhr.onabort = () => {
      reject(new DOMException("aborted", "AbortError"));
    };
    if (signal.aborted) {
      xhr.abort();
      return;
    }
    signal.addEventListener(
      "abort",
      () => {
        xhr.abort();
      },
      { once: true },
    );
    xhr.send(bytes);
  });
}

export function realAttachmentUploadWire(): AttachmentUploadWire {
  return {
    createFile: async (input, idempotencyKey) => {
      const res = unwrap(
        await client.POST("/v1/files", {
          params: { header: { "Idempotency-Key": idempotencyKey } },
          body: {
            purpose: "sales_attachment",
            content_type: input.contentType,
            byte_size: input.byteSize,
            sha256: input.sha256,
            file_name: input.fileName,
          },
        }),
        "Could not start the upload",
      );
      return {
        fileId: res.file.id,
        uploadUrl: res.upload_url,
        contentType: res.file.content_type,
      };
    },
    putBytes: putWithProgress,
    storeFile: async (fileId, idempotencyKey) => {
      unwrap(
        await client.POST("/v1/files/{id}/store", {
          params: { path: { id: fileId }, header: { "Idempotency-Key": idempotencyKey } },
        }),
        "Could not check the uploaded file",
      );
    },
    attach: async (body, idempotencyKey) => {
      const res = await client.POST("/v1/sales-attachments", {
        params: { header: { "Idempotency-Key": idempotencyKey } },
        body: {
          file_id: body.fileId,
          [SUBJECT_KEY[body.subject.kind]]: body.subject.id,
          category: body.category,
          ...(body.title !== undefined ? { title: body.title } : {}),
        },
      });
      unwrap(res, "Could not attach the file");
    },
  };
}

/** Did the server answer this failure, so a resend under the same key would
 * replay the same refusal? A transport loss, a 5xx and 409
 * `IDEMPOTENCY_IN_PROGRESS` stored nothing (`lib/attempt-key.ts`). */
export function answeredRefusal(error: unknown): boolean {
  if (!(error instanceof RequestError)) return false;
  if (error.httpStatus >= 500) return false;
  return error.code !== "IDEMPOTENCY_IN_PROGRESS";
}
